import {
  DeleteOutlined,
  EditOutlined,
  EnterOutlined,
  MoreOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { Avatar, Button, Dropdown, Flex, Space, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import type {
  Message,
  MessageAttachment,
  Participant,
  PendingMessage
} from "../types/messaging.types.ts"
import { AttachmentCard } from "./AttachmentCard.tsx"
import { MessageBody } from "./MessageBody.tsx"

interface MessageRowProps {
  message: Message
  participants: Participant[]
  isOwn: boolean
  /** True when the previous message is from the same person, minutes apart. */
  grouped: boolean
  isHighlighted: boolean
  searchTerm?: string
  canSaveToProjectFiles: boolean
  onReply: (message: Message) => void
  onEdit: (message: Message, body: string) => void
  onDelete: (message: Message) => void
  onJumpToMessage: (messageId: string) => void
  onSaveToProjectFiles: (attachment: MessageAttachment) => void
}

export function MessageRow({
  message,
  participants,
  isOwn,
  grouped,
  isHighlighted,
  searchTerm,
  canSaveToProjectFiles,
  onReply,
  onEdit,
  onDelete,
  onJumpToMessage,
  onSaveToProjectFiles
}: MessageRowProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(message.body)

  const actions = [
    {
      key: "reply",
      icon: <EnterOutlined />,
      label: t("messages.actions.reply", "Reply")
    },
    ...(isOwn && !message.is_deleted
      ? [
          {
            key: "edit",
            icon: <EditOutlined />,
            label: t("messages.actions.edit", "Edit")
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            danger: true,
            label: t("messages.actions.delete", "Delete")
          }
        ]
      : [])
  ]

  return (
    <div
      id={`message-${message.id}`}
      data-testid={"message-row"}
      className={`group flex gap-2.5 px-4 ${grouped ? "pt-0.5" : "pt-3"} ${
        isOwn ? "flex-row-reverse" : ""
      } ${isHighlighted ? "bg-orange-50" : ""}`}
    >
      {/* A repeat from the same person keeps the column but drops the avatar,
          so a run of messages reads as one block. */}
      <div className={"w-7 shrink-0"}>
        {!grouped && (
          <Avatar src={message.sender?.avatar || undefined} size={28}>
            {message.sender?.full_name?.[0] ?? "?"}
          </Avatar>
        )}
      </div>

      {/* `flex-1 min-w-0` is what actually bounds the column — a bare
          `max-w` percentage resolves against the row and still let a long
          unbroken run push past the panel on a narrow screen. */}
      <div
        className={`flex min-w-0 flex-1 flex-col ${
          isOwn ? "items-end" : "items-start"
        }`}
      >
        {!grouped && (
          <Flex
            align={"baseline"}
            gap={6}
            className={isOwn ? "flex-row-reverse" : ""}
          >
            <Typography.Text strong className={"text-[13px]"}>
              {isOwn
                ? t("messages.you", "You")
                : (message.sender?.full_name ??
                  t("messages.unknownSender", "Former member"))}
            </Typography.Text>
            {!isOwn && message.sender?.role_label && (
              <Typography.Text type={"secondary"} className={"text-[11px]"}>
                {message.sender.role_label}
              </Typography.Text>
            )}
            <Typography.Text type={"secondary"} className={"text-[11px]"}>
              {dayjs(message.created).format("h:mm A")}
            </Typography.Text>
          </Flex>
        )}

        <Flex align={"center"} gap={4} className={isOwn ? "flex-row-reverse" : ""}>
          <div
            className={`mt-0.5 max-w-[42rem] overflow-hidden break-words rounded-xl px-3 py-2 text-sm ${
              isOwn ? "bg-[#E6F4F3] text-slate-800" : "bg-slate-50 text-slate-800"
            }`}
          >
            {message.reply_to && (
              <button
                type={"button"}
                onClick={() => onJumpToMessage(message.reply_to!.id)}
                className={
                  "mb-1.5 block w-full cursor-pointer rounded-md border-l-2 border-teal-500 bg-white/70 px-2 py-1 text-left"
                }
              >
                <Typography.Text className={"block text-[11px]"} strong>
                  {message.reply_to.sender_name}
                </Typography.Text>
                <Typography.Text
                  className={"block text-[11px]"}
                  type={"secondary"}
                  ellipsis
                >
                  {message.reply_to.is_deleted
                    ? t("messages.deletedPlaceholder", "This message was deleted.")
                    : message.reply_to.body}
                </Typography.Text>
              </button>
            )}

            {editing ? (
              <div className={"min-w-[18rem]"}>
                <TextArea
                  disableAutoCapitalize
                  value={draft}
                  autoSize={{ minRows: 1, maxRows: 8 }}
                  onChange={(event) => setDraft(event.target.value)}
                  aria-label={t("messages.actions.edit", "Edit")}
                />
                <Space className={"mt-2"}>
                  <Button
                    size={"small"}
                    type={"primary"}
                    disabled={!draft.trim()}
                    onClick={() => {
                      onEdit(message, draft)
                      setEditing(false)
                    }}
                  >
                    {t("common.save", "Save")}
                  </Button>
                  <Button
                    size={"small"}
                    onClick={() => {
                      setDraft(message.body)
                      setEditing(false)
                    }}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                </Space>
              </div>
            ) : (
              <>
                <MessageBody
                  body={message.body}
                  isDeleted={message.is_deleted}
                  participants={participants}
                  mentionNames={message.mentioned_names}
                  highlight={searchTerm}
                />
                {message.edited_at && !message.is_deleted && (
                  <Typography.Text type={"secondary"} className={"ml-1.5 text-[10px]"}>
                    {t("messages.editedLabel", "Edited")}
                  </Typography.Text>
                )}
              </>
            )}
          </div>

          {/* Compact, and only on hover or keyboard focus. */}
          {!message.is_deleted && !editing && (
            <Flex
              className={
                "shrink-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
              }
            >
              <Tooltip title={t("messages.actions.reply", "Reply")}>
                <Button
                  type={"text"}
                  size={"small"}
                  icon={<EnterOutlined />}
                  onClick={() => onReply(message)}
                  aria-label={t("messages.actions.reply", "Reply")}
                />
              </Tooltip>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: actions,
                  onClick: ({ key }) => {
                    if (key === "reply") onReply(message)
                    if (key === "edit") setEditing(true)
                    if (key === "delete") onDelete(message)
                  }
                }}
              >
                <Button
                  type={"text"}
                  size={"small"}
                  icon={<MoreOutlined />}
                  aria-label={t("messages.actions.more", "Message actions")}
                />
              </Dropdown>
            </Flex>
          )}
        </Flex>

        {!message.is_deleted &&
          message.attachments.map((attachment) => (
            <div key={attachment.id} className={isOwn ? "flex justify-end" : ""}>
              <AttachmentCard
                attachment={attachment}
                canSaveToProjectFiles={canSaveToProjectFiles}
                onSaveToProjectFiles={onSaveToProjectFiles}
              />
            </div>
          ))}
      </div>
    </div>
  )
}

interface PendingRowProps {
  pending: PendingMessage
  onRetry: (pending: PendingMessage) => void
  onDiscard: (clientToken: string) => void
}

/** A message the server has not acknowledged: sending, or failed with a retry. */
export function PendingMessageRow({
  pending,
  onRetry,
  onDiscard
}: PendingRowProps) {
  const { t } = useTranslation()
  const failed = pending.status === "failed"

  return (
    <div
      className={"flex flex-row-reverse gap-2.5 px-4 pt-3"}
      data-testid={"pending-row"}
    >
      <div className={"w-7 shrink-0"} />
      <div className={"flex min-w-0 flex-1 flex-col items-end"}>
        <div
          className={
            "max-w-[42rem] overflow-hidden break-words rounded-xl bg-[#E6F4F3]/60 px-3 py-2 text-sm"
          }
        >
          <Typography.Text className={"block whitespace-pre-wrap"}>
            {pending.body}
          </Typography.Text>
          {pending.attachment_names.length > 0 && (
            <Typography.Text type={"secondary"} className={"block text-[11px]"}>
              {pending.attachment_names.join(", ")}
            </Typography.Text>
          )}
        </div>
        <Flex align={"center"} gap={6} justify={"flex-end"} className={"mt-0.5"}>
          <Typography.Text
            type={failed ? "danger" : "secondary"}
            className={"text-[11px]"}
          >
            {failed
              ? t("messages.status.failed", "Not sent")
              : t("messages.status.sending", "Sending…")}
          </Typography.Text>
          {failed && (
            <>
              <Button
                size={"small"}
                type={"link"}
                icon={<ReloadOutlined />}
                onClick={() => onRetry(pending)}
              >
                {t("messages.actions.retry", "Retry")}
              </Button>
              <Button
                size={"small"}
                type={"link"}
                onClick={() => onDiscard(pending.client_token)}
              >
                {t("common.discard", "Discard")}
              </Button>
            </>
          )}
        </Flex>
      </div>
    </div>
  )
}
